import { ALGORITHM_VERSION, EXPLORATORY_WARNING, type AnalysisResultDto } from "@gazelab/shared";
import { DIVERGING_A, DIVERGING_B, sequentialColor } from "@gazelab/analysis";
import { drawImageWithOverlay, overlayToCanvas, type OverlaySpec } from "../../lib/heatmapCanvas.js";

export const EXPORT_LONG_SIDE = 1200;

export interface ExportPanel {
  title: string;
  overlay: OverlaySpec | null;
}

export interface ExportMeta {
  studyTitle: string;
  imageName: string;
  analysis: AnalysisResultDto;
  legend: "sequential" | "diverging";
  threshold: number;
}

const rgb = (c: readonly number[]) => `rgb(${c[0]},${c[1]},${c[2]})`;

/** Compone uno o varios paneles (imagen + mapa) con leyenda y pie de metadatos en un PNG. */
export async function composePng(image: HTMLImageElement, panels: ExportPanel[], meta: ExportMeta): Promise<Blob> {
  const nw = image.naturalWidth;
  const nh = image.naturalHeight;
  const scale = EXPORT_LONG_SIDE / Math.max(nw, nh);
  const pw = Math.round(nw * scale);
  const ph = Math.round(nh * scale);
  const gap = 24;
  const pad = 24;
  const titleH = 34;
  const legendH = 60;
  const a = meta.analysis;
  const footer = [
    `Estudio: ${meta.studyTitle} · Imagen: ${meta.imageName} (${nw}×${nh})`,
    `A: ${a.groupA.groupName} (n=${a.groupA.n}) · B: ${a.groupB.groupName} (n=${a.groupB.n}) · Fuente: ${a.params.source === "synthetic" ? "SINTÉTICO (demostración)" : "webcam (WebGazer)"}`,
    `sigma=${a.params.sigma} · rejilla=${a.params.gridLongSide} · algoritmo ${a.algorithmVersion ?? ALGORITHM_VERSION} · generado ${new Date().toISOString()}`,
    EXPLORATORY_WARNING,
  ];
  const footerH = footer.length * 22 + 12;
  const W = pad * 2 + panels.length * pw + (panels.length - 1) * gap;
  const H = pad + titleH + ph + 12 + legendH + footerH + pad;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  ctx.textBaseline = "top";

  panels.forEach((p, i) => {
    const x = pad + i * (pw + gap);
    ctx.fillStyle = "#0f172a";
    ctx.font = "600 20px system-ui, sans-serif";
    ctx.fillText(p.title, x, pad, pw);
    drawImageWithOverlay(ctx, image, p.overlay ? overlayToCanvas(p.overlay) : null, x, pad + titleH, pw, ph);
    ctx.strokeStyle = "#94a3b8";
    ctx.strokeRect(x + 0.5, pad + titleH + 0.5, pw - 1, ph - 1);
  });

  // Leyenda
  const ly = pad + titleH + ph + 16;
  const lw = Math.min(420, W - pad * 2);
  if (meta.legend === "sequential") {
    const g = ctx.createLinearGradient(pad, 0, pad + lw, 0);
    for (let i = 0; i <= 8; i++) g.addColorStop(i / 8, rgb(sequentialColor(0.15 + (0.85 * i) / 8)));
    ctx.fillStyle = g;
    ctx.fillRect(pad, ly, lw, 14);
    ctx.fillStyle = "#334155";
    ctx.font = "15px system-ui, sans-serif";
    ctx.fillText(`Distribución de atención estimada · escala compartida A/B · máx. ${(a.sharedMax * 100).toExponential(2)} % por celda`, pad, ly + 20, W - pad * 2);
  } else {
    const g = ctx.createLinearGradient(pad, 0, pad + lw, 0);
    g.addColorStop(0, rgb(DIVERGING_B));
    g.addColorStop(0.5, "rgba(255,255,255,1)");
    g.addColorStop(1, rgb(DIVERGING_A));
    ctx.fillStyle = g;
    ctx.fillRect(pad, ly, lw, 14);
    ctx.strokeStyle = "#cbd5e1";
    ctx.strokeRect(pad, ly, lw, 14);
    ctx.fillStyle = "#334155";
    ctx.font = "15px system-ui, sans-serif";
    ctx.fillText(
      `Naranja: más relativo en B (${a.groupB.groupName}) · Azul: más relativo en A (${a.groupA.groupName}) · umbral ${Math.round(meta.threshold * 100)} % del máx. abs.`,
      pad,
      ly + 20,
      W - pad * 2,
    );
  }

  ctx.font = "14px system-ui, sans-serif";
  footer.forEach((line, i) => {
    ctx.fillStyle = i === footer.length - 1 ? "#92400e" : "#475569";
    ctx.fillText(line, pad, ly + legendH + i * 22, W - pad * 2);
  });

  return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error("No se pudo generar el PNG"))), "image/png"));
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
